// Typed wrappers over the broker document-intake BFF (BRK-S038 / PMS-S131). A
// broker uploads one or more PDFs (any mix of ACORD 125/126/130 + prior
// policies); PMS classifies + extracts them server-side and `POST …/process`
// returns a PROPOSAL the SPA uses to PREPOPULATE the New Business flow. The bytes
// go straight to storage via a presigned PUT — they never pass through the PMS
// app tier, and the SPA holds no storage credentials (NN #5).
//
// CONTRACT: types + paths are derived from pms-local docs/openapi.yaml on the
// `feat/PMS-S131-broker-document-intake-bff` branch (own-scoped, capability-gated
// under the caller's brokerage). The routes are:
//   POST /api/broker/documents                       (presign; open/reuse batch)
//   POST /api/broker/documents/{id}/confirm          (confirm upload landed)
//   POST /api/broker/documents/{id}/extract          (per-doc extract; NOT used by
//        the prefill flow — `/process` extracts internally — kept for reference)
//   GET  /api/broker/intake-batches/{id}             (poll the batch + its docs)
//   POST /api/broker/intake-batches/{id}/process     (fold → the proposal)
// A contract-drift guard (test/lib/brokerDocumentsContract.test.ts) pins these
// so the seam + dev mock can never silently diverge from the contract again.
import { ApiError } from "@/lib/api";
import type { AuthedRequester } from "@/lib/brokerAuth";

const DOCUMENTS = "/api/broker/documents";
const BATCHES = "/api/broker/intake-batches";

/** A recognized intake document kind (null until classified; `other` =
 *  recognized-but-unmapped). Mirrors the PMS document `kind` enum. */
export type IntakeDocumentKind =
  | "acord_125"
  | "acord_126"
  | "acord_130"
  | "prior_policy"
  | "other"
  | null;

/** The async-shaped batch status the SPA can poll (mirrors the PMS batch enum).
 *  `/process` returns the settled status alongside the proposal. */
export type IntakeBatchStatus = "open" | "processing" | "processed" | "failed";

/** Per-document extraction outcome. */
export type ExtractionStatus = "pending" | "extracted" | "failed";

/** Where a batch's proposal is targeted.
 *
 *  `bond` is PMS-S269's widening (its `intake_target` enum gains a bond member):
 *  a bond-request PDF is NOT a `submission` packet, because reusing that target
 *  routes it through the P&C merge buckets. It is a CONSUMER-side widening only
 *  in the sense that BRK now sends it — PMS deploys first, or the presign 400s
 *  on the enum (the paired-deploy rule). */
export type IntakeTarget = "submission" | "account" | "bond";

/** The presigned PUT the browser uploads the PDF with (bytes bypass the app tier). */
export interface UploadTicket {
  url: string;
  method: "PUT";
  headers: Record<string, string>;
  expiresIn: number;
}

/** POST /api/broker/documents — the upload ticket (ids + presigned PUT). */
export interface PresignResponse {
  batchId: string;
  documentId: string;
  storageKey: string;
  upload: UploadTicket;
}

/** One document in the batch, as the confirm + batch reads report it (the full
 *  contract shape). */
export interface IntakeDocument {
  id: string;
  batchId: string;
  kind: IntakeDocumentKind;
  filename: string;
  contentType: string;
  storageKey: string;
  byteSize: number | null;
  sha256: string | null;
  storedAt: string | null;
  uploadedByEmail: string;
  accountId: string | null;
  submissionId: string | null;
  extractionStatus: ExtractionStatus;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

/** The batch envelope (its poll-shaped status). Own-scoped: carries the owning
 *  `brokerageId`. */
export interface IntakeBatch {
  id: string;
  target: IntakeTarget;
  status: IntakeBatchStatus;
  uploadedByEmail: string;
  brokerageId: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

/** GET /api/broker/intake-batches/{id} — the batch + its documents. The proposal
 *  is NOT here; it comes from `POST …/process`. */
export interface IntakeBatchView {
  batch: IntakeBatch;
  documents: IntakeDocument[];
}

// ─── The proposal (POST …/process) ────────────────────────────────────────────

/** A page range within a document that couldn't be classified/read — surfaced to
 *  the broker so nothing is silently dropped ("pages 4–6 couldn't be read"). */
export interface UnrecognizedRange {
  documentId: string;
  filename: string;
  pageStart: number;
  pageEnd: number;
}

/** A document that failed to process entirely (bad scan, encrypted, …). */
export interface DocumentFailure {
  documentId: string;
  filename: string;
  error: string;
}

/** A recognized form the merge couldn't map onto the proposal. */
export interface UnmappedForm {
  documentId: string;
  kind: string;
}

/** A field two documents disagreed on; `chosen` is the value the merge kept. */
export interface FieldConflict {
  path: string;
  options: { documentId: string; kind: string; value?: unknown }[];
  chosen?: unknown;
}

/** The report-back of everything extraction could NOT cleanly use — never dropped.
 *  (Note: unmatched class codes are NOT here — they live inline in
 *  `proposed.wc.exposures` as rows with a raw code but no resolved id; the prefill
 *  mapping derives them, see src/lib/documentPrefill.ts.) */
export interface IntakeReport {
  unrecognized: UnrecognizedRange[];
  unmapped: UnmappedForm[];
  failures: DocumentFailure[];
  conflicts: FieldConflict[];
}

/** The proposed payload, split by target. `account`/`submission`/`gl`/`wc` are
 *  opaque bags the prefill mapping (src/lib/documentPrefill.ts) narrows onto the
 *  account record + the manifest field bag; `priorCarriers`/`losses` are
 *  display-only for now (no form home yet — BRK-S038 scope note). WC exposure
 *  rows carry `codeId` where a class code matched. */
export interface IntakeProposed {
  account: Record<string, unknown>;
  submission: Record<string, unknown>;
  gl: Record<string, unknown>;
  wc: Record<string, unknown>;
  priorCarriers: unknown[];
  losses: unknown[];
  /**
   * BRK-S120 / PMS-S269 — the BOND-request extraction's content, and it lives
   * HERE, beside the other targets, NOT at the envelope's top level.
   *
   * ⚠️ It was declared at the top level until BRK-S123, and how that survived
   * is the lesson: each half was tested against its own belief about the shape.
   * BRK's fixtures and dev stub put the record at `proposal.values`; PMS put it
   * at `proposed.bond.values`; every test passed on both sides; and the first
   * real upload read `undefined` and told the broker "we couldn't read the
   * document" over a proposal carrying all twelve fields. The fixtures for this
   * shape are captured from a REAL `/process` response for exactly that reason.
   */
  bond?: { values?: Record<string, unknown> };
}

/** An existing own account the insured might already be (match-only; the broker
 *  decides — BRK-S038 doesn't wire attach). */
export interface AccountMatchCandidate {
  id: string;
  accountNumber: string;
  legalName: string;
  dbaName: string | null;
  city: string;
  state: string;
  brokerageName: string;
}

export interface AccountMatch {
  match: "candidates" | "none";
  candidates?: AccountMatchCandidate[];
}

/** Whether the proposed account clears the server's floor, and what's missing. */
export interface AccountReadiness {
  ready: boolean;
  missing: string[];
  issues: { path: string; message: string }[];
}

/** Per proposed-field provenance: which document/kind a value came from. */
export type Provenance = Record<string, { documentId: string; kind: string }>;

/** The full proposal a processed batch yields (`POST …/process`, returned
 *  synchronously with a settled `status`). */
export interface IntakeProposal {
  batchId: string;
  status: IntakeBatchStatus;
  target: IntakeTarget;
  /**
   * BRK-S120 / PMS-S269 — the bond extraction's content is ONE FLAT record
   * keyed by INTAKE FIELD KEY, values in WIRE form (ISO dates, plain numbers —
   * D-10). It lives at `proposed.bond.values`; see `IntakeProposed.bond` for
   * where a top-level `values` here went wrong (BRK-S123).
   *
   * ABSENT is the honest spelling of "the document didn't state it": there is no
   * null member and no zero. An absent record at all means extraction produced
   * nothing usable — the flow degrades to manual entry (never an error state
   * the broker has to clear). `unknown` values because a producer can send
   * anything: `src/lib/bondDocumentPrefill.ts` is the only reader, and it drops
   * whatever a field's own rules reject rather than coercing it.
   */
  proposed: IntakeProposed;
  provenance: Provenance;
  coverageFormsFound: string[];
  accountMatch: AccountMatch;
  accountReadiness: AccountReadiness;
  report: IntakeReport;
}

// ─── Requests ─────────────────────────────────────────────────────────────────

/** The presign body. The contract (`documentUploadRequestSchema`) requires
 *  EXACTLY ONE of `target` | `batchId`: the FIRST file in a packet sends
 *  `target` (which opens the batch — the ticket returns its `batchId`), every
 *  SUBSEQUENT file sends that `batchId` (joining the same batch). Never neither,
 *  never both — the discriminated union makes that a compile-time guarantee. */
export type PresignInput = {
  filename: string;
  contentType: "application/pdf";
} & (
  | { target: IntakeTarget; batchId?: never }
  | { batchId: string; target?: never }
);

/** POST /api/broker/documents — open (target) or join (batchId) an intake batch,
 *  record a document row, and return a presigned PUT. PDF-only. */
export function presignDocument(
  request: AuthedRequester,
  input: PresignInput,
): Promise<PresignResponse> {
  return request<PresignResponse>(DOCUMENTS, { method: "POST", body: input });
}

/** POST /api/broker/documents/{id}/confirm — verify the object landed in storage
 *  (records size; optional client sha256). */
export function confirmDocument(
  request: AuthedRequester,
  documentId: string,
  body: { sha256?: string } = {},
): Promise<IntakeDocument> {
  return request<IntakeDocument>(
    `${DOCUMENTS}/${encodeURIComponent(documentId)}/confirm`,
    { method: "POST", body },
  );
}

/** GET /api/broker/intake-batches/{id} — the batch + its documents (poll for the
 *  upload/extraction status; the proposal comes from `processBatch`). */
export function getBatch(
  request: AuthedRequester,
  batchId: string,
): Promise<IntakeBatchView> {
  return request<IntakeBatchView>(`${BATCHES}/${encodeURIComponent(batchId)}`);
}

/** POST /api/broker/intake-batches/{id}/process — fold the batch (classify →
 *  extract → merge) into the proposal, returned synchronously with a settled
 *  `status`. Proposal only — never writes account/submission data. */
export function processBatch(
  request: AuthedRequester,
  batchId: string,
): Promise<IntakeProposal> {
  return request<IntakeProposal>(
    `${BATCHES}/${encodeURIComponent(batchId)}/process`,
    { method: "POST" },
  );
}

// ─── Direct-to-storage upload (not the authed PMS requester) ──────────────────

/** Progress event while a file uploads to storage. */
export interface UploadProgress {
  loaded: number;
  total: number;
}

/** Uploads one file to its presigned PUT. Kept behind this alias so callers
 *  (and tests) can inject a fake — the dev-login offline path swaps in a
 *  no-network simulator (the fetch stub can't see a direct storage PUT). */
export type Uploader = (
  ticket: UploadTicket,
  file: File,
  opts?: { onProgress?: (p: UploadProgress) => void; signal?: AbortSignal },
) => Promise<void>;

/**
 * PUT the file bytes straight to the presigned storage URL. Uses XHR (not fetch)
 * so `xhr.upload.onprogress` gives real per-file progress — fetch can't report
 * upload progress cross-browser. No auth header, no base-URL join: the URL +
 * headers come verbatim from the presign ticket.
 */
export const uploadToPresigned: Uploader = (ticket, file, opts) =>
  new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(ticket.method, ticket.url);
    for (const [key, value] of Object.entries(ticket.headers)) {
      xhr.setRequestHeader(key, value);
    }
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        opts?.onProgress?.({ loaded: e.loaded, total: e.total });
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new ApiError(xhr.status, `Upload failed (${xhr.status})`));
    };
    xhr.onerror = () =>
      reject(
        new ApiError(0, "Upload failed — check your connection and try again."),
      );
    xhr.onabort = () => reject(new ApiError(0, "Upload cancelled."));
    if (opts?.signal) {
      opts.signal.addEventListener("abort", () => xhr.abort());
    }
    xhr.send(file);
  });

// ─── Client-side upload guardrails (mirror the contract's limits) ─────────────

/** Max upload size — mirrors the PMS 32 MB cap so an over-limit file is rejected
 *  before it's uploaded (the server enforces the same, plus the 100-page cap the
 *  client can't cheaply count). */
export const MAX_UPLOAD_BYTES = 32 * 1024 * 1024;

/** Why a dropped file was rejected before upload. */
export type FileRejectionReason = "not_pdf" | "too_large";

/** Screen a dropped file against the PDF-only + size guards. Returns null when
 *  it's acceptable, else the reason (the dropzone shows a per-file message). */
export function screenFile(file: File): FileRejectionReason | null {
  const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name);
  if (!isPdf) return "not_pdf";
  if (file.size > MAX_UPLOAD_BYTES) return "too_large";
  return null;
}
